import { ILegalEntityContractProps } from "./legalEntityContractTab";
import { ContractTableRow } from "../../contract/components/ContractTable.types";
import { ContractFilterStatus, ContractStatus } from "../../contract/Contracts.types";
import { useTranslation } from "@localization/useTranslation.ts";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, hasDateRange, matchesDateFilter, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { FilterOperatorUi } from "@common/api/filterTypes.ts";
import { getUniqueFilterOptions, getUniqueNestedFilterOptions, sortByName } from "@common/hooks/filterUtils.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { useRefetchWhenPaneSaves } from "../../main/panes/usePanes";

export default function useLegalEntityContractTabHooks(props: ILegalEntityContractProps) {
    const { translateString, translateContractStatusEnum } = useTranslation();
    const url = `/legalEntity/${props.legalEntityId}/contracts`;
    const { isLoading, data: contracts, refetch } = useDotLegalQuery<Array<ContractTableRow>>(url);

    function refetchData() {
        refetch();
        props.onContractsChanged();
    }

    // The create panel lives above the router and has no onSave prop to call, so the tab hands it the
    // refetch to run once a contract has been created behind it.
    useRefetchWhenPaneSaves(refetchData);

    const {
        selectFilters: filters,
        dateFilters,
        filterOperators,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useFilterState(`legalEntity-${props.legalEntityId}-contracts-filters`);

    const allRows = contracts ?? [];

    const filteredRows = applyClientFilters(
        allRows,
        [
            { id: "contract", candidate: (r) => r.id },
            { id: "contractType", candidate: (r) => r.contractType ?? "" },
            { id: "businessArea", candidate: (r) => r.businessAreas.map((ba) => ba.id) },
            { id: "contractManager", candidate: (r) => r.contractManager },
            { id: "internalContractParties", candidate: (r) => r.internalContractParties.map((p) => p.id) },
        ],
        filters,
        {},
        filterOperators,
        (row) => matchesStatusFilter(row) && matchesDateFilter(dateFilters.endDate, row.endDate)
    );

    function matchesStatusFilter(row: ContractTableRow): boolean {
        if ((filters.status?.length ?? 0) === 0) return true;

        const selectedStatuses = filters.status;
        const matchesStatus = selectedStatuses.includes(row.status.toString());
        const matchesExpired = selectedStatuses.includes(ContractFilterStatus.Expired.toString()) && row.isOverdue;
        const hasAnyMatch = matchesStatus || matchesExpired;
        const isNegative = filterOperators.status === FilterOperatorUi.Negative;

        return isNegative ? !hasAnyMatch : hasAnyMatch;
    }

    function getFilterOptions(): Array<FilterOption> {
        return withOperators(
            [
                {
                    id: "contract",
                    label: translateString("contract"),
                    type: FilterOptionType.SelectField,
                    value: filters.contract ?? [],
                    fixedData: getUniqueFilterOptions(
                        allRows,
                        (r) => `${r.userFriendlyId} - ${r.name}`,
                        (r) => r.id
                    ).sort(sortByName),
                },
                {
                    id: "contractType",
                    label: translateString("contractType"),
                    type: FilterOptionType.SelectField,
                    value: filters.contractType ?? [],
                    fixedData: getUniqueFilterOptions(allRows, (r) => r.contractType ?? undefined).sort(sortByName),
                },
                {
                    id: "businessArea",
                    label: translateString("businessArea"),
                    type: FilterOptionType.SelectField,
                    value: filters.businessArea ?? [],
                    fixedData: getUniqueNestedFilterOptions(
                        allRows,
                        (r) => r.businessAreas,
                        (ba) => ba.name,
                        (ba) => ba.id
                    ).sort(sortByName),
                },
                {
                    id: "contractManager",
                    label: translateString("contractManager"),
                    type: FilterOptionType.SelectField,
                    value: filters.contractManager ?? [],
                    fixedData: getUniqueFilterOptions(allRows, (r) => r.contractManager).sort(sortByName),
                },
                {
                    id: "internalContractParties",
                    label: translateString("internalContractParties"),
                    type: FilterOptionType.SelectField,
                    value: filters.internalContractParties ?? [],
                    fixedData: getUniqueNestedFilterOptions(
                        allRows,
                        (r) => r.internalContractParties,
                        (p) => p.name,
                        (p) => p.id
                    ).sort(sortByName),
                },
                {
                    id: "endDate",
                    label: translateString("endDate"),
                    type: FilterOptionType.DateField,
                    value: dateFilters.endDate ?? "",
                },
                {
                    id: "status",
                    label: translateString("status"),
                    type: FilterOptionType.SelectField,
                    value: filters.status ?? [],
                    fixedData: [
                        { id: ContractStatus.Draft.toString(), name: translateContractStatusEnum(ContractStatus.Draft.toString()) },
                        { id: ContractStatus.Approved.toString(), name: translateContractStatusEnum(ContractStatus.Approved.toString()) },
                        { id: ContractStatus.Archived.toString(), name: translateContractStatusEnum(ContractStatus.Archived.toString()) },
                        { id: ContractFilterStatus.Expired.toString(), name: translateString("expired") },
                    ],
                },
            ],
            filterOperators
        );
    }

    const hasActiveFilters = Object.values(filters).some((v) => v.length > 0) || Object.values(dateFilters).some((v) => hasDateRange(v));

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        isLoading,
        contracts: filteredRows,
        hasData: allRows.length > 0,
        refetchData,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
    };
}
